import { useEffect, useState, type ReactNode } from 'react';
import { Button, Code, HStack, Input, Table, Text, Textarea } from '@chakra-ui/react';
import { ChromePage, ChromeSection } from '../ui/chrome_page.tsx';
import { collectDiagnostics } from './diagnostics.ts';
import type { EchoTestResult } from './echo_test.ts';
import { capabilityReport, echoTest, type SpikeReport } from './spike.ts';
import { VideoSpikePanel } from './video_spike_panel.tsx';

const rows = (diagnostics: ReturnType<typeof collectDiagnostics>) : Row[] => [
    ['Shell', diagnostics.host ? `${diagnostics.host.shell} ${diagnostics.host.shellVersion} (${diagnostics.host.platform})` : 'none (plain browser)', diagnostics.host !== null],
    ['Secure context', String(diagnostics.secureContext), diagnostics.secureContext],
    ['VideoDecoder (WebCodecs)', String(diagnostics.videoDecoder), diagnostics.videoDecoder],
    ['OffscreenCanvas', String(diagnostics.offscreenCanvas), diagnostics.offscreenCanvas],
    ['Worker', String(diagnostics.worker), diagnostics.worker],
    ['WebSocket', String(diagnostics.webSocket), diagnostics.webSocket],
    ['User agent', diagnostics.userAgent, null],
];

type Row = [label: string, value: string, ok: boolean | null];

const answer = (value: boolean | string) : Row[1] => (typeof value === 'string' ? `error: ${value}` : String(value));

function capabilityRows(report: SpikeReport) : Row[] {
    const { sandbox, codecs } = report;
    const worker = sandbox.worker;

    return [
        ['Native bridge', String(report.bridge), report.bridge],
        ...codecs.map(
            (c) : Row => [
                c.label,
                `hardware: ${answer(c.hardware)}, software: ${answer(c.software)}`,
                c.hardware === true || c.software === true,
            ],
        ),
        ...(worker
            ? ([
                    ['Worker: secure context', String(worker.secureContext), worker.secureContext],
                    ['Worker: VideoDecoder', String(worker.videoDecoder), worker.videoDecoder],
                    ['Worker: OffscreenCanvas 2D', String(worker.offscreenCanvas2d), worker.offscreenCanvas2d],
                    ['Worker: fetch', worker.fetch, worker.fetch.startsWith('blocked')],
                    ['Worker: WebSocket', worker.webSocket, !worker.webSocket.startsWith('allowed')],
                    ['Worker: CSP violations', worker.violations.join('; ') || 'none', worker.violations.length > 0],
                ] satisfies Row[])
            : ([['Worker probe', sandbox.error ?? 'no answer', false]] satisfies Row[])),
        ['Page: fetch (control)', sandbox.pageFetch, sandbox.pageFetch.startsWith('allowed')],
        ['Worker sandboxed (connect-src none)', String(sandbox.sandboxed), sandbox.sandboxed],
    ];
}

function echoRows(result: EchoTestResult) : Row[] {
    return [
        ['Result', result.ok ? 'pass' : `fail: ${result.error ?? 'unknown'}`, result.ok],
        ['Bytes echoed', `${result.received} of ${result.bytes}`, result.received === result.bytes],
        ['Open', `${result.openMs.toFixed(0)} ms`, null],
        ['Round trip', `${result.roundTripMs.toFixed(0)} ms`, null],
        ['Throughput (both directions)', `${result.mbitPerSecond.toFixed(1)} Mbit/s`, null],
    ];
}

function ResultTable({ rows }: { rows: Row[] }) : ReactNode {
    return (
        <Table.Root size="sm" variant="line">
            <Table.Body>
                {rows.map(([label, value, ok]) => (
                    <Table.Row key={label}>
                        <Table.Cell fontWeight="medium" whiteSpace="nowrap" verticalAlign="top">
                            {label}
                        </Table.Cell>
                        <Table.Cell color={ok === null ? undefined : ok ? 'green.400' : 'red.400'} wordBreak="break-word">
                            {value}
                        </Table.Cell>
                    </Table.Row>
                ))}
            </Table.Body>
        </Table.Root>
    );
}

function stored(key: string, fallback: string) : string {
    try {
        return localStorage.getItem(key) ?? fallback;
    } catch {
        return fallback;
    }
}

export function DiagnosticsPage() : ReactNode {
    const diagnostics = collectDiagnostics(globalThis as unknown as Record<string, unknown>);
    const [report, setReport] = useState<SpikeReport | null>(null);
    // The Android emulator reaches the development machine at 10.0.2.2.
    const [host, setHost] = useState(() =>
        stored('echoHost', diagnostics.host?.shell === 'android' ? '10.0.2.2' : '127.0.0.1'),
    );
    const [port, setPort] = useState(() => stored('echoPort', '7007'));
    const [megabytes, setMegabytes] = useState('1');
    const [echo, setEcho] = useState<EchoTestResult | 'running' | null>(null);

    useEffect(() => {
        let isCurrent = true;
        const probe = async () : Promise<void> => {
            const result = await capabilityReport();
            if (isCurrent) {
                setReport(result);
            }
        };
        void probe();

        return () => {
            isCurrent = false;
        };
    }, []);

    async function runEcho() {
        try {
            localStorage.setItem('echoHost', host);
            localStorage.setItem('echoPort', port);
        } catch {
            // storage is optional
        }
        setEcho('running');
        setEcho(
            await echoTest({ host, port: Number(port), bytes: Math.round(Number(megabytes) * 1024 * 1024) }),
        );
    }

    const full = report && { ...report, ...(echo && echo !== 'running' ? { echo } : {}) };

    return (
        <ChromePage title="Tilez – host diagnostics">
            <ResultTable rows={rows(diagnostics)} />

            <ChromeSection title="RTSP stream (iterations I2 and I3)">
                <VideoSpikePanel />
            </ChromeSection>

            <ChromeSection title="Engine capabilities (spike item 1b)">
                {report ? <ResultTable rows={capabilityRows(report)} /> : <Text>Probing…</Text>}
            </ChromeSection>

            <ChromeSection title="TCP echo through net.tcp">
                <Text fontSize="sm" color="fg.muted">
                    Start <Code>node scripts/tcp-echo.mjs</Code> on the development machine first.
                </Text>
                <HStack flexWrap="wrap">
                    <Input width="48" aria-label="Echo host" value={host} onChange={(event) => setHost(event.target.value)} />
                    <Input width="20" aria-label="Echo port" inputMode="numeric" value={port} onChange={(event) => setPort(event.target.value)} />
                    <Input width="16" aria-label="Size in MiB" inputMode="decimal" value={megabytes} onChange={(event) => setMegabytes(event.target.value)} />
                    <Text fontSize="sm">MiB</Text>
                    <Button variant="outline" loading={echo === 'running'} disabled={!report?.bridge} onClick={() => void runEcho()}>
                        Run
                    </Button>
                </HStack>
                {echo && echo !== 'running' && <ResultTable rows={echoRows(echo)} />}
            </ChromeSection>

            <ChromeSection title="Report (for docs/spike-results.md)">
                <Textarea
                    readOnly
                    rows={12}
                    fontFamily="mono"
                    fontSize="xs"
                    value={full ? JSON.stringify(full, null, 2) : ''}
                    onFocus={(event) => event.target.select()}
                />
            </ChromeSection>
        </ChromePage>
    );
}
